/**
 * Pure helpers that collapse the N per-pair `pair_matchup_count` rows
 * into a single synthetic "Number of Matchups per Team" summary row for
 * the constraints table.
 *
 * Why: the data model writes one constraint per team-pair (the solver
 * needs that granularity), but for the constraints table that's
 * unwieldy at scale — 50 teams = 1225 rows. The matrix modal is the
 * intended editor for this constraint type, so the table only needs
 * to surface a summary that points users at the modal.
 *
 * Pure module on purpose so the collapse logic is unit-testable
 * without dragging React/Emotion through Jest.
 */

import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'
import type { SavedDivisionTargetRow } from './matchupMatrixHelpers'

export const PAIR_MATCHUP_COUNT_CODE = 'pair_matchup_count'

/** Synthetic row id — keep stable so the table can key off it. */
export const COLLAPSED_PAIR_MATCHUP_ID = '__collapsed_pair_matchup__'

/** Synthetic row id for the canonical division-target summary row. */
export const COLLAPSED_DIVISION_TARGET_ID = '__collapsed_division_target__'

/**
 * Sort slot for the synthetic division-target row. High enough to sit at the
 * bottom of the user-ordered real constraints, below the 9000+ placeholders —
 * so the row lands in a stable, predictable position.
 */
export const COLLAPSED_DIVISION_TARGET_SORT_ORDER = 8500

/**
 * Marker shape grafted onto the SchedulerConstraint-like row so the
 * column renderers can branch on it. Carries the underlying ids so
 * action handlers can fan out (bulk delete, bulk toggle).
 */
export interface CollapsedPairMatchupRow {
  /** Discriminator. */
  _isCollapsed: true
  /** Stable synthetic id. */
  id: string
  /** Constraint-shape fields the table renderers expect. */
  type: {
    id: string
    code: string
    name: string
    category: string
  }
  is_hard: boolean
  is_enabled: boolean
  description: string | null
  division_uids: string[] | null
  team_uids: string[] | null
  sort_order: number
  parameters: Record<string, unknown>
  /** Underlying constraint ids the synthetic row represents. */
  _collapsedIds: string[]
  /** Pre-computed display strings so the column renderers stay simple. */
  _summary: {
    pairCount: number
    countSummary: string
    scopeSummary: string
    isHardMajorityHard: boolean
    enabledCount: number
    disabledCount: number
  }
}

/**
 * Build the summary fields for the collapsed row. Pulled out so tests
 * can pin the description copy without rendering anything.
 */
export function summarizePairMatchupConstraints(
  constraints: SchedulerConstraint[]
): CollapsedPairMatchupRow['_summary'] {
  const pairCount = constraints.length

  // Most-common count — group by parameters.count.
  const countFreq: Record<number, number> = {}
  for (const c of constraints) {
    const n = c.parameters.count
    if (typeof n === 'number') {
      countFreq[n] = (countFreq[n] ?? 0) + 1
    }
  }
  const distinctCounts = Object.keys(countFreq).length
  let topCount: number | null = null
  let topCountHits = 0
  for (const [k, v] of Object.entries(countFreq)) {
    if (v > topCountHits) {
      topCountHits = v
      topCount = Number(k)
    }
  }
  const countSummary =
    distinctCounts === 0
      ? 'no counts set'
      : distinctCounts === 1
      ? `${topCount} games each`
      : `counts vary (mostly ${topCount})`

  // Scope: tally rows with division_uids null/[] vs. scoped.
  let globalCount = 0
  let scopedCount = 0
  for (const c of constraints) {
    if (c.division_uids === null || c.division_uids.length === 0) {
      globalCount += 1
    } else {
      scopedCount += 1
    }
  }
  const scopeSummary =
    globalCount > 0 && scopedCount > 0
      ? 'mixed scope'
      : globalCount > 0
      ? 'league-wide'
      : 'per-division'

  // is_hard majority — used for the HARD/SOFT badge on the summary row.
  let hardCount = 0
  for (const c of constraints) {
    if (c.is_hard) {
      hardCount += 1
    }
  }
  const isHardMajorityHard = hardCount * 2 >= pairCount

  let enabledCount = 0
  for (const c of constraints) {
    if (c.is_enabled) {
      enabledCount += 1
    }
  }

  return {
    pairCount,
    countSummary,
    scopeSummary,
    isHardMajorityHard,
    enabledCount,
    disabledCount: pairCount - enabledCount,
  }
}

/**
 * Build the synthetic CollapsedPairMatchupRow from the underlying
 * pair_matchup_count constraints. Returns null when the input is empty
 * (caller should not insert a row in that case).
 */
export function buildCollapsedPairMatchupRow(
  constraints: SchedulerConstraint[]
): CollapsedPairMatchupRow | null {
  if (constraints.length === 0) {
    return null
  }

  const summary = summarizePairMatchupConstraints(constraints)

  // Union of division_uids across all rows (null means "applies to all"
  // and dominates — if any row is global, the summary reads as global).
  const divUidSet = new Set<string>()
  let anyGlobal = false
  for (const c of constraints) {
    if (c.division_uids === null || c.division_uids.length === 0) {
      anyGlobal = true
    } else {
      for (const u of c.division_uids) {
        divUidSet.add(u)
      }
    }
  }
  const divisionUids = anyGlobal ? null : Array.from(divUidSet)

  // Union of unique team_uids across all rows (each row has 2 teams).
  const teamUidSet = new Set<string>()
  for (const c of constraints) {
    for (const u of c.team_uids ?? []) {
      teamUidSet.add(u)
    }
  }

  // Borrow the first constraint's `type` block — code/name/category
  // are identical across all pair_matchup_count rows.
  const proto = constraints[0]

  return {
    _isCollapsed: true as const,
    id: COLLAPSED_PAIR_MATCHUP_ID,
    type: {
      id: proto.type.id,
      code: proto.type.code,
      name: proto.type.name,
      category: proto.type.category,
    },
    is_hard: summary.isHardMajorityHard,
    // The summary row reads as "enabled" if any underlying row is enabled —
    // matches the toggle's intent (clicking off bulk-disables all).
    is_enabled: summary.enabledCount > 0,
    description: null,
    division_uids: divisionUids,
    team_uids: Array.from(teamUidSet),
    sort_order: Math.min(...constraints.map((c) => c.sort_order)) || 0,
    parameters: {},
    _collapsedIds: constraints.map((c) => c.id),
    _summary: summary,
  }
}

// ===========================================================================
// Canonical division-target summary row
//
// The Games Per Team modal now persists explicit per-division targets to the
// `scheduler_division_targets` table (GET/POST /division-targets) and deletes
// the legacy pair_matchup_count rows it used to write. The constraints list
// surfaces those targets as a single synthetic "Games Per Team" row built
// here — sourced from /division-targets, NOT from pair_matchup_count.
//
// Only EXPLICIT targets (games_per_team / division_total with a positive
// number) get a row. Divisions left on the season-filling default
// (fill_feasible_slots, or a null number) produce nothing — keeps the list
// quiet for the common case.
// ===========================================================================

/**
 * Synthetic type block for the target row. There's no underlying constraint
 * to borrow from, so the code/name/category are constants. Keeping
 * `code: pair_matchup_count` lets the existing display-name + category
 * mappings ("Games per Team" / "Game") resolve without special-casing.
 */
const DIVISION_TARGET_TYPE = {
  id: '',
  code: PAIR_MATCHUP_COUNT_CODE,
  name: 'Games Per Team',
  category: 'fairness',
} as const

/**
 * Marker shape for the division-target summary row. Uses a DISTINCT
 * discriminator (`_isCollapsedTarget`) so the existing `_isCollapsed`
 * branches/handlers for the pair_matchup_count row never fire on it.
 */
export interface CollapsedDivisionTargetRow {
  /** Discriminator — distinct from the pair-matchup row's `_isCollapsed`. */
  _isCollapsedTarget: true
  /** Stable synthetic id. */
  id: string
  /** Constraint-shape fields the table renderers expect. */
  type: {
    id: string
    code: string
    name: string
    category: string
  }
  is_hard: boolean
  is_enabled: boolean
  description: string | null
  division_uids: string[] | null
  /**
   * Always `null` (NOT `[]`). The table's team filter treats a non-null empty
   * array as "scoped to no teams" and would hide the row; `null` keeps a
   * division-level target always visible. Scope is still expressed via
   * `division_uids`.
   */
  team_uids: null
  sort_order: number
  parameters: Record<string, unknown>
  /** Pre-computed display chip ("N per team" / "N total" / multi-summary). */
  _targetSummary: string
}

/**
 * A target is "displayable" — worth a row — only when it sets an explicit
 * games_per_team / division_total value (>= 1). fill_feasible_slots and null
 * numbers are the quiet default and produce no row.
 */
function isDisplayableTarget(t: SavedDivisionTargetRow): boolean {
  return (
    (t.target_mode === 'games_per_team' ||
      t.target_mode === 'division_total') &&
    typeof t.target_number === 'number' &&
    t.target_number >= 1
  )
}

/**
 * Division uids that carry an explicit displayable target. Used to (a)
 * suppress legacy pair_matchup_count display rows for those divisions and
 * (b) suppress the pair_matchup_count placeholder when a target row exists —
 * so the target row is the single source of truth for that division.
 */
export function divisionUidsWithTargets(
  targets: SavedDivisionTargetRow[]
): Set<string> {
  const out = new Set<string>()
  for (const t of targets) {
    if (isDisplayableTarget(t)) {
      out.add(t.division_uid)
    }
  }
  return out
}

/**
 * Is this pair_matchup_count row superseded by a division target — i.e. does
 * its division now carry an explicit target, so the row should be hidden from
 * (and excluded from bulk actions on) the collapsed legacy "Games per Team"
 * row?
 *
 *   - Scoped rows (division_uids set) resolve directly via division_uids.
 *   - Global / unscoped rows resolve via their teams: superseded only when
 *     BOTH teams sit in targeted divisions (a cross-division global row that
 *     straddles a non-targeted division is kept).
 *
 * Returns false when there are no targets (nothing to supersede).
 */
export function isPairRowSupersededByTarget(
  constraint: Pick<SchedulerConstraint, 'division_uids' | 'team_uids'>,
  targetDivisionUids: Set<string>,
  teamDivisionByUid: Map<string, string>
): boolean {
  if (targetDivisionUids.size === 0) {
    return false
  }
  const divUids = constraint.division_uids
  if (divUids && divUids.length > 0) {
    return divUids.some((uid) => targetDivisionUids.has(uid))
  }
  const teamDivs = (constraint.team_uids ?? [])
    .map((uid) => teamDivisionByUid.get(uid))
    .filter((d): d is string => !!d)
  if (teamDivs.length === 0) {
    return false
  }
  return teamDivs.every((d) => targetDivisionUids.has(d))
}

/** The mode-aware "core" of a target chip, ignoring derived totals. */
function targetChipCore(t: SavedDivisionTargetRow): string {
  if (t.target_mode === 'division_total') {
    return `${t.target_number} total`
  }
  return `${t.target_number} per team`
}

/**
 * Build the synthetic "Games Per Team" summary row from the saved
 * per-division targets. Returns null when no division carries a displayable
 * target (caller should not insert a row).
 *
 * `divisionMeta` provides team counts so a single games_per_team target can
 * surface its derived total ("· X total games", X = floor(teamCount * N / 2),
 * per the games-per-team-communication convention).
 */
export function buildCollapsedDivisionTargetRow(
  targets: SavedDivisionTargetRow[],
  divisionMeta: { id: string; name: string; teamCount: number }[]
): CollapsedDivisionTargetRow | null {
  const displayable = targets.filter(isDisplayableTarget)
  if (displayable.length === 0) {
    return null
  }

  const metaById = new Map(divisionMeta.map((m) => [m.id, m]))
  const divisionUids = displayable.map((t) => t.division_uid)

  let summary: string
  if (displayable.length === 1) {
    const t = displayable[0]
    const meta = metaById.get(t.division_uid)
    if (
      t.target_mode === 'games_per_team' &&
      meta &&
      meta.teamCount >= 2 &&
      typeof t.target_number === 'number'
    ) {
      // CEIL — the same rounding the backend applies, so the chip's "total
      // games" never reads one below what generation actually produces.
      const total = Math.ceil((meta.teamCount * t.target_number) / 2)
      summary = `${t.target_number} per team · ${total} total games`
    } else {
      summary = targetChipCore(t)
    }
  } else {
    // Multi-division: collapse to the shared chip core when every targeted
    // division agrees, otherwise a plain count.
    const cores = new Set(displayable.map(targetChipCore))
    summary =
      cores.size === 1
        ? `${[...cores][0]} · ${displayable.length} divisions`
        : `${displayable.length} divisions targeted`
  }

  return {
    _isCollapsedTarget: true as const,
    id: COLLAPSED_DIVISION_TARGET_ID,
    type: { ...DIVISION_TARGET_TYPE },
    // A games-per-team / division-total target is a HARD requirement: the
    // solver sizes the schedule to produce exactly that many games (built-in,
    // not an optional soft penalty).
    is_hard: true,
    is_enabled: true,
    description: null,
    division_uids: divisionUids,
    team_uids: null,
    sort_order: COLLAPSED_DIVISION_TARGET_SORT_ORDER,
    parameters: {},
    _targetSummary: summary,
  }
}
